import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import Form from '@controls/Form';

import { FormValues } from '../../../../types';
import { getFieldName, isNumberArray, isPriceRangeDefault } from '../../../../utils';
import styles from '../../FiltersPopup.module.scss';
import { transformPopupIsPreOrderToIsPreOrder } from '../../utils';
import PopupFormContentInner from './PopupFormContentInner';
import { PopupFormContentProps } from './types';
import { parseFiltersHistoryFromUrl, reorderFormValues } from './utils';

const PopupFormContent: FC<PopupFormContentProps> = ({
    initialValues,
    onApplyFilters,
    onClose,
    filterData,
    hidePriceFilter,
    isBrandPage = false,
    priceRange,
    fieldPrefix,
    rangeDefault,
    formKey,
    isOpen,
    baseFilters: baseFiltersFromProps,
    onApplyClick,
}) => {
    const { query } = useRouter();

    const [rememberedPrice, setRememberedPrice] = useState<number[] | undefined>(() => {
        const priceFieldName = getFieldName('price', fieldPrefix);
        const priceValue = initialValues[priceFieldName];
        const savedPrice = priceValue !== undefined && isNumberArray(priceValue) ? priceValue : undefined;
        if (savedPrice && priceRange) {
            const [min, max] = savedPrice;
            if (!isPriceRangeDefault(min, max, priceRange)) {
                return savedPrice;
            }
        }

        return undefined;
    });

    const lastChangedBlockRef = useRef<string[]>([]);

    const handleFormSubmit = useCallback(
        (values: FormValues) => {
            const transformedValues = transformPopupIsPreOrderToIsPreOrder(values, fieldPrefix);
            const lastChangedBlockCode =
                lastChangedBlockRef.current.length > 0
                    ? lastChangedBlockRef.current[lastChangedBlockRef.current.length - 1]
                    : undefined;
            const reorderedValues = reorderFormValues(transformedValues, lastChangedBlockRef.current, fieldPrefix);

            onApplyFilters(reorderedValues, lastChangedBlockCode);
            onClose();

            lastChangedBlockRef.current = [];
        },
        [fieldPrefix, onApplyFilters, onClose]
    );

    useEffect(() => {
        if (formKey) {
            const historyFromUrl = parseFiltersHistoryFromUrl(query);
            lastChangedBlockRef.current = historyFromUrl;
        }
    }, [formKey, query]);

    return (
        <Form
            key={formKey}
            initialValues={initialValues}
            enableReinitialize={false}
            onSubmit={handleFormSubmit}
            className={styles.form}
        >
            <PopupFormContentInner
                filterData={filterData}
                hidePriceFilter={hidePriceFilter}
                isBrandPage={isBrandPage}
                priceRange={priceRange}
                fieldPrefix={fieldPrefix}
                rangeDefault={rangeDefault}
                formKey={formKey}
                isOpen={isOpen}
                baseFiltersFromProps={baseFiltersFromProps}
                onClose={onClose}
                onApplyClick={onApplyClick}
                rememberedPrice={rememberedPrice}
                setRememberedPrice={setRememberedPrice}
                lastChangedBlockRef={lastChangedBlockRef}
            />
        </Form>
    );
};

export default PopupFormContent;
